import { useMemo, useState } from "react";
import { useApp } from "../app/AppContext";
import type { Note } from "../lib/api";
import { pad, todayKey } from "../lib/date";
import { readJotSort, sortJots } from "../lib/jots";
import { JotRow } from "./JotRow";

function dayLabel(dayDate: string, today: string): string {
  if (dayDate === today) return "Today";
  const y = new Date(`${today}T00:00:00`);
  y.setDate(y.getDate() - 1);
  const yKey = `${y.getFullYear()}-${pad(y.getMonth() + 1)}-${pad(y.getDate())}`;
  if (dayDate === yKey) return "Yesterday";
  return new Date(`${dayDate}T00:00:00`).toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

/** Read-only browser of past days' jots. Grouped by day, newest first. */
export function JotsHistoryModal({ onClose }: { onClose: () => void }) {
  const app = useApp();
  const today = todayKey();

  const sort = readJotSort();
  const days = useMemo(() => {
    const m = new Map<string, Note[]>();
    for (const n of app.notes) {
      if (!n.dayDate) continue;
      const arr = m.get(n.dayDate) ?? [];
      arr.push(n);
      m.set(n.dayDate, arr);
    }
    return [...m.entries()]
      .map(([day, list]) => ({ day, list: sortJots(list, sort) }))
      .sort((a, b) => b.day.localeCompare(a.day));
  }, [app.notes, sort]);

  const [sel, setSel] = useState<string | null>(days[0]?.day ?? null);
  const selected = days.find((d) => d.day === sel) ?? days[0] ?? null;

  return (
    <div className="ovl" onMouseDown={onClose}>
      <div className="jhist" onMouseDown={(e) => e.stopPropagation()}>
        <div className="jhist-head">
          <span>↺ Jots history</span>
          <button className="jhist-x" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        {days.length === 0 ? (
          <div className="jhist-empty">No jots yet.</div>
        ) : (
          <div className="jhist-body">
            <div className="jhist-days">
              {days.map((d) => (
                <button
                  key={d.day}
                  className={`jhist-day${d.day === selected?.day ? " on" : ""}`}
                  onClick={() => setSel(d.day)}
                >
                  <span>{dayLabel(d.day, today)}</span>
                  <span className="jhist-c">{d.list.length}</span>
                </button>
              ))}
            </div>

            <div className="jhist-notes">
              {selected && (
                <>
                  <div className="jhist-daytitle">
                    {new Date(`${selected.day}T00:00:00`).toLocaleDateString(undefined, {
                      weekday: "long",
                      day: "numeric",
                      month: "long",
                    })}
                    <span className="jhist-sub"> · {selected.list.length} jots</span>
                  </div>
                  {selected.list.map((j) => (
                    <JotRow key={j.id} jot={j} onLeave={onClose} />
                  ))}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
